import React, { useState } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import DatePicker from "components/atoms/DatePicker/DatePicker";

import Input from "components/atoms/Input/Input";
import Radio from "components/atoms/Radio/Radio";
import Select from "components/atoms/Select/Select";
import errorToast from "helpers/errorToast";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import { submitTask } from "api";
import testIds from "constants/testIds";
import "./TaskDialog.scss";
import { useGetTaskDetails } from "api/queries/task/useGetTaskDetails";

const mapTitleToTranslations = (intl, title) => {
  const translationsByTitle = {
    "cost recovery for shipment ": intl.formatMessage({
      id: "tasks.taskDialog.costRecoveryForShipment",
      defaultMessage: "Cost recovery for shipment ",
    }),
  };
  return translationsByTitle[title.toLowerCase()] ?? title;
};

const TaskDialog = function ({ isOpen, close, taskId }) {
  const intl = useIntl();

  const [searchParams] = useSearchParams();
  const status = searchParams.get("status") || "";
  const isArchived = status === "done";

  const methods = useForm({ shouldFocusError: true });
  const { setValue, watch, getValues, handleSubmit, reset } = methods;
  watch();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: task = {}, isLoading } = useGetTaskDetails(taskId, {
    ...(isArchived && {
      config: {
        params: {
          archived: true,
        },
      },
    }),
    options: {
      enabled: isOpen,
      onError: (error) => {
        errorToast(error, "Could not retrieve task. Please try again later.");
      },
    },
  });

  /** [POST] Handles the submission for the task survey */
  const handleTaskSubmit = () => {
    setIsSubmitting(true);

    const questionHandlers = {
      multiple_choices: ({ field_id, options }) => {
        const value = options
          .filter(({ key }) => getValues(key)) // Filter selected options
          .map(({ key }) => key)
          .join(", ");
        return { field_id, value };
      },
      "yes/no": ({ field_id }) => {
        const value = getValues(field_id);
        const comment = value === "Yes" ? getValues(`${field_id}_comment`) : "";
        return { field_id, value, comment };
      },
      default: ({ field_id }) => ({ field_id, value: getValues(field_id) }),
    };

    const payload = [];
    task.questions.forEach((question) => {
      const handler = questionHandlers[question.type] || questionHandlers.default;
      payload.push(handler(question));
    });

    submitTask(taskId, payload)
      .then(() => window.location.reload())
      .catch((error) => errorToast(error))
      .finally(() => setIsSubmitting(false));
  };

  /** Returns an input based on a provided type */
  const generateInput = ({ mandatory, type, field_id, options }) => {
    switch (type) {
      case "text":
      case "numerical":
        return (
          <Input
            type={type === "text" ? "text" : "number"}
            name={field_id}
            required={mandatory}
            eventContext="Task Dialog"
            className="rounded-pill input-width-lg h-26"
          />
        );
      case "yes/no":
        if (!getValues(field_id)) setValue(field_id, "No");
        return (
          <Radio
            options={[
              {
                value: "Yes",
                label: intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" }),
              },
              {
                value: "No",
                label: intl.formatMessage({ id: "general.no", defaultMessage: "No" }),
              },
            ]}
            name={field_id}
            defaultChecked={getValues(field_id)}
            customOption
            className="border-0 w-50 task-dialog-radio"
            eventContext="Task Dialog"
          />
        );
      case "multiple_choices":
        return options.map(({ key, label }) => (
          <div className="pb-2" key={key}>
            <Checkbox
              checkboxLabel={label}
              name={`${key}`}
              required={false}
              providerSections
              // customOnChange={() => handleFreightModeChange(value)}
            />
          </div>
        ));
      case "date_picker":
        return <DatePicker name={field_id} required={mandatory} eventContext="Task Dialog" />;
      case "dropdown":
        return <Select name={field_id} options={options} eventContext="Task Dialog" />;
      default:
        return null;
    }
  };

  /** Renders a label + input for the question arg */
  const renderQuestion = (question) => {
    const { field_id, label, mandatory } = question;
    return (
      <Row key={field_id} className="mb-3">
        <Col lg={4}>
          <label className={mandatory ? "required" : ""}>{label}</label>
        </Col>
        <Col lg={8}>
          {generateInput(question)}
          {question.type === "yes/no" && getValues(question.field_id) === "Yes" && (
            <div>
              <Input
                type="text"
                name={`${question.field_id}_comment`}
                placeholder={question.comment_placeholder}
                required={question.is_comment_mandatory}
                eventContext="Task Dialog"
                className="rounded-pill input-width-lg h-26"
              />
            </div>
          )}
        </Col>
      </Row>
    );
  };

  const renderDoneAnswers = () => {
    if (!task?.answers) return null;
    return Object.entries(task?.answers).map(([key, value]) => (
      <Row key={key} className="mb-md">
        <Col sm={4}>
          <div className="font-weight-bold">{key}:</div>
        </Col>
        <Col sm={8}>
          <div>{value}</div>
        </Col>
      </Row>
    ));
  };

  const formattedTitle = () => {
    if (!task.shipment_id) return "";
    const shipmentIdLength = task.shipment_id.length;
    const formattedTitle = mapTitleToTranslations(intl, task.name.slice(0, -shipmentIdLength));

    return (
      <p className="d-flex mb-0">
        <span className="mr-2">{formattedTitle}</span>
        <Link to={`/shipment/${task.shipment_id}/tracking`} target="_blank" rel="noopener noreferrer">
          {task.shipment_id}
        </Link>
      </p>
    );
  };

  const handleClose = () => {
    reset();
    close();
  };

  return (
    <Dialog size="md" title={formattedTitle()} show={isOpen} onHide={handleClose} isLoading={isLoading}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleTaskSubmit)}>
          {!isArchived ? task?.questions?.map((question) => renderQuestion(question)) : renderDoneAnswers()}

          <div className="d-block border-grey border-top w-100 my-xl" />

          {!isArchived ? (
            <div className="d-flex align-items-center justify-content-between">
              <Button
                variant="outline-primary"
                label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
                onClick={handleClose}
              />
              <Button
                isLoading={isSubmitting}
                className="ml-auto"
                variant="success"
                label={intl.formatMessage({ id: "tasks.taskDialog.submitSurvey", defaultMessage: "Submit survey" })}
                type="submit"
                data-testid={testIds.TASK_DIALOG_SUBMIT_BUTTON}
              />
            </div>
          ) : (
            <div className="d-flex justify-content-end">
              <Button
                label={intl.formatMessage({ id: "components.organisms.dialog.close", defaultMessage: "Close" })}
                onClick={handleClose}
              />
            </div>
          )}
        </form>
      </FormProvider>
    </Dialog>
  );
};

TaskDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  taskId: PropTypes.number.isRequired,
};

export default TaskDialog;
